import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonDirective } from '@coreui/angular-pro';
import { AuthService } from 'auth';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import { take } from 'rxjs/operators';

import { FAMILY_ACCEPT_ERRORS, FamilyService, FamilyTokenInfo } from '../../../services/family.service';
import {
  FAMILY_BUNDLE_PLAN_CODES, FAMILY_PLAN_SLUG, clearFamilyToken, consumeFamilyAutoAccept, consumeLoginReturn,
  markFamilyAutoAccept, rememberFamilyToken, returnHereAfterLogin, validFamilyToken, wizardQuery
} from '../../../services/funnel-intent';
import { PRODUCT_PRICES } from '../../../services/product-label';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

/**
 * 'loading'       — reading the link's public status
 * 'valid'         — a usable invitation: offer / accept
 * 'accepting'     — POST accept running (or automatically after the sign-in)
 * 'dead'          — expired / revoked / used / invalid link (no CTA but the website)
 * 'not_available' — the feature is switched off (404)
 * 'error'         — anything else (network)
 */
type Phase = 'loading' | 'valid' | 'accepting' | 'dead' | 'not_available' | 'error';

/**
 * Landing of a DNA Family SPECIAL invitation: /:lang/family/join/:token
 * (docs/globalhealth/DNA_FAMILY_SPEZIAL_MACHBARKEIT.md §4.4).
 *
 * Shows who invites, the product, $999 one-time, the "no voluntary money-back
 * guarantee" notice and the contracting party. Then:
 *   signed in         -> "Accept and continue" -> POST accept -> the wizard (`next`)
 *   no account        -> the direct checkout (/r?plan=dna_family_special&family=<token>)
 *   existing account  -> sign in, come back here, accept automatically
 *
 * Public route; RouteService treats `family/join` as public so the token URL is
 * never recorded as anyone's homeUrl.
 */
@Component({
  selector: 'app-family-join-page',
  standalone: true,
  imports: [CommonModule, ButtonDirective, TranslatePipe, FunnelShellComponent],
  templateUrl: './family-join-page.component.html',
  styleUrls: ['./family-join-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FamilyJoinPageComponent {
  private route      = inject(ActivatedRoute);
  private router     = inject(Router);
  private family     = inject(FamilyService);
  private auth       = inject(AuthService);
  private i18n       = inject(I18nService);
  private translate  = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  phase      = signal<Phase>('loading');
  info       = signal<FamilyTokenInfo | null>(null);
  signedIn   = signal(false);
  /** Explanation of an accept refusal (self invitation, already a DNA buyer, …). */
  acceptError = signal<string | null>(null);

  readonly fallbackPrice = PRODUCT_PRICES.dna_family_special.once;
  private readonly token: string | null;

  constructor() {
    this.token = validFamilyToken(this.route.snapshot.paramMap.get('token'));
    if (!this.token) {
      this.phase.set('dead');
      this.info.set({ status: 'invalid' });
      return;
    }
    rememberFamilyToken(this.token);
    const autoAccept = consumeFamilyAutoAccept(this.token);
    // Back from "sign in and come back here": restore the member's own home URL.
    if (autoAccept) consumeLoginReturn();

    this.family.tokenStatus(this.token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: info => {
          this.info.set(info);
          if (info.status !== 'valid') {
            this.phase.set('dead');
            return;
          }
          this.auth.getUser$().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(user => {
            this.signedIn.set(!!user);
            this.phase.set('valid');
            if (user && (autoAccept || info.accepted_by_you)) this.accept();
          });
        },
        error: err => {
          // A backend that reports a dead link with an error status still names it
          // in `status`; a plain 404 (`not_available`) means the switch is off.
          const status = err?.error?.status;
          if (['expired', 'revoked', 'used', 'invalid'].includes(status)) {
            this.info.set({ status });
            this.phase.set('dead');
            return;
          }
          this.phase.set(err?.status === 404 ? 'not_available' : 'error');
        }
      });
  }

  /** Signed in: bind the invitation to this account, then on into the wizard. */
  accept(): void {
    if (!this.token || this.phase() === 'accepting') return;
    this.phase.set('accepting');
    this.acceptError.set(null);
    this.family.accept(this.token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          clearFamilyToken();
          const next = res.next?.startsWith('/') ? res.next : null;
          if (next) {
            this.router.navigateByUrl(`/${this.lang()}${next}`, { replaceUrl: true });
          } else {
            this.router.navigate(['/', this.lang(), 'membership', 'apply'], {
              queryParams: wizardQuery(FAMILY_BUNDLE_PLAN_CODES[0]), replaceUrl: true
            });
          }
        },
        error: err => {
          this.phase.set('valid');
          if (err?.status === 401) {
            // The session is gone: sign in and come back.
            this.signedIn.set(false);
            return;
          }
          const code: string | undefined = err?.error?.code;
          this.acceptError.set(code && FAMILY_ACCEPT_ERRORS.includes(code)
            ? this.translate.translate(`family_join.errors.${code}`)
            : (err?.error?.error || this.translate.translate('family_join.errors.generic')));
        }
      });
  }

  /** No account yet: the direct checkout creates it and binds the invitation. */
  createAccount(): void {
    if (!this.token) return;
    this.router.navigate(['/', this.lang(), 'r'], { queryParams: { plan: FAMILY_PLAN_SLUG, family: this.token } });
  }

  /** Existing account: sign in, come back here, accept automatically. */
  signIn(): void {
    if (!this.token) return;
    markFamilyAutoAccept(this.token);
    returnHereAfterLogin(`/${this.lang()}/family/join/${this.token}`);
    this.router.navigate(['/', this.lang(), 'login']);
  }

  /** The `family_join.dead.<status>` key of a link that cannot be used. */
  deadKey(): string {
    const status = this.info()?.status ?? 'invalid';
    return `family_join.dead.${status === 'valid' ? 'invalid' : status}`;
  }

  price(): number | string {
    return this.info()?.price ?? this.fallbackPrice;
  }

  lang(): string {
    return this.i18n.getLanguage() || 'en';
  }
}
